import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CockpitAutoStop, CockpitLeverResult, CockpitPolicyView, CockpitPreviewView, CockpitResultsView } from '@vello/api-client';
import { autoStopTone, formatMarketInstant, marketTimeZone, previewLeverRows, previewRows, recentDays, roiPercent, roiTone, windowsToMinutes } from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, Field, FilterBar, Notice, Panel, Select, Stack, StatCard, KpiGrid, Table, Text, useAutoRefresh } from '@vello/web-theme';
import { wireMoney } from './shared';

/**
 * The results of the cockpit (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 section 3.5), two answers:
 *
 *  1. WHAT EACH LEVER RETURNED on a finished market-local day - the spend, the contribution it ADDED over its control group (net of the spend; negative = it lost money), the return, how the
 *     comparison was made and how large the sample was - and where the AUTOMATIC SHUTDOWN stands for it. All of it comes through D496's measurement read port: with no reader connected, a
 *     market without a measurement policy or a sample below its floor the panel says so in words (never a zero that looks like a result).
 *  2. "IF IT WERE ACTIVE YESTERDAY": what the controller policy in force would have made of yesterday's signal windows - how long each zone would have been in each state and which levers the
 *     rule table would have called for. A classification preview over the sampled windows: pacing, the fund and the kills are not replayed, and nothing is saved.
 * A refused read is an ERROR, never an empty table. The data refreshes itself.
 */
/** A calendar day (`YYYY-MM-DD`) as a date in the operator's language - the value stays ISO, the label never does. */
const dayLabel = (day: string, lang: string): string => {
  const at = Date.parse(`${day}T12:00:00.000Z`);
  return Number.isNaN(at) ? day : new Intl.DateTimeFormat(lang, { timeZone: 'UTC', dateStyle: 'medium' }).format(new Date(at));
};

export function ResultsPanel({ market }: { readonly market: string }): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, reason, fail } = useVocab();
  const tz = marketTimeZone(market, markets);
  // The finished days are recomputed on every refresh: a console left open across midnight offers the new day without a reload.
  const [days, setDays] = useState<readonly string[]>(() => recentDays(Date.now(), tz, 7));
  const [day, setDay] = useState(() => days[0] ?? '');
  const [results, setResults] = useState<CockpitResultsView | undefined>(undefined);
  const [policy, setPolicy] = useState<CockpitPolicyView | undefined>(undefined);
  const [sim, setSim] = useState<CockpitPreviewView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [simError, setSimError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  // Another market (or its zone) starts at its newest finished day.
  useEffect(() => {
    const fresh = recentDays(Date.now(), tz, 7);
    setDays(fresh);
    setDay(fresh[0] ?? '');
  }, [market, tz]);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    setDays(recentDays(Date.now(), tz, 7));
    if (market === '' || day === '') {
      setResults(undefined);
      return;
    }
    const [res, pol] = await Promise.all([runtime.incentiveCockpit.results(market, day), runtime.incentiveCockpit.policy(market)]);
    if (mine !== seq.current) return;
    if (res.ok) {
      setResults(res.value);
      setError(undefined);
    } else {
      setResults(undefined);
      setError(t('cpt.results.error', { code: fail(res.error) }));
    }
    if (pol.ok) setPolicy(pol.value);
  }, [runtime, market, day, tz, t, fail]);

  useEffect(() => {
    setResults(undefined);
    setSim(undefined);
    setSimError(undefined);
    void load();
    // Reload when the header scope or the day changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, day]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' && day !== '' });

  const simulate = async (): Promise<void> => {
    if (policy?.value === null || policy?.value === undefined) return;
    setBusy(true);
    const res = await runtime.incentiveCockpit.preview(market, policy.value);
    setBusy(false);
    if (!res.ok) return setSimError(t('cpt.results.simError', { code: fail(res.error) }));
    if (!res.value.valid) return setSimError(t('cpt.rules.refused', { why: v('controller.policy_issue', res.value.issue) }));
    setSimError(undefined);
    setSim(res.value);
  };

  const when = (iso: string | null): string => (iso === null ? '—' : formatMarketInstant(iso, tz, lang));
  const stateOf = (lever: string, stops: readonly CockpitAutoStop[]): CockpitAutoStop | undefined => stops.find((s) => s.lever === lever);

  return (
    <Stack gap={4}>
      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.results.title')}</h3>
          <Text role="caption" tone="tertiary">{t('cpt.results.hint')}</Text>
          <FilterBar label={t('cpt.results.filter')}>
            <Field label={t('cpt.results.day')} htmlFor="cpt-day">
              <Select id="cpt-day" value={day} onChange={(ev) => setDay(ev.target.value)}>
                {(days.includes(day) ? days : [day, ...days]).map((d) => (
                  <option key={d} value={d}>{dayLabel(d, lang)}</option>
                ))}
              </Select>
            </Field>
          </FilterBar>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
          {error === undefined && results === undefined && <EmptyState title={t('ui.loading')} />}
          {results !== undefined && !results.available && (
            <Notice tone="warning">{t('cpt.results.unavailable', { why: v('measurement.unavailable_reason', results.reason) })}</Notice>
          )}
          {results !== undefined && results.available && (
            <Stack gap={3}>
              <KpiGrid>
                <StatCard label={t('cpt.results.totalSpend')} value={wireMoney(results.totalSpend, lang)} />
                <StatCard
                  label={t('cpt.results.totalReturn')}
                  value={results.totalIncrementalContribution === null ? t('cpt.results.notMeasured') : wireMoney(results.totalIncrementalContribution, lang)}
                  tone={results.totalIncrementalContribution === null ? undefined : results.totalIncrementalContribution.amountMinor.startsWith('-') ? 'danger' : 'success'}
                />
              </KpiGrid>
              <Table
                columns={[
                  { key: 'lever', header: t('ctl.log.col.lever'), render: (r: CockpitLeverResult) => <span>{v('measurement.lever', r.lever)}</span> },
                  { key: 'method', header: t('cpt.results.col.method'), render: (r: CockpitLeverResult) => <span>{v('measurement.method', r.method)}</span> },
                  { key: 'spend', header: t('cpt.results.col.spend'), numeric: true, render: (r: CockpitLeverResult) => <span className="tnum">{wireMoney(r.spend, lang)}</span> },
                  {
                    key: 'net', header: t('cpt.results.col.net'), numeric: true,
                    render: (r: CockpitLeverResult) => (r.incrementalContribution === null ? <span>—</span> : <span className="tnum">{wireMoney(r.incrementalContribution, lang)}</span>),
                  },
                  {
                    key: 'roi', header: t('cpt.results.col.roi'),
                    render: (r: CockpitLeverResult) =>
                      r.roiBp === null ? (
                        <Stack gap={1}>
                          <span>—</span>
                          {r.unavailableReason !== null && <Text role="caption" tone="tertiary">{v('measurement.unavailable_reason', r.unavailableReason)}</Text>}
                        </Stack>
                      ) : (
                        <Badge tone={roiTone(r.roiBp)}><span className="tnum">{t('cpt.results.roi', { pct: roiPercent(r.roiBp) })}</span></Badge>
                      ),
                  },
                  {
                    key: 'sample', header: t('cpt.results.col.sample'), numeric: true,
                    render: (r: CockpitLeverResult) => <span className="tnum">{t('cpt.results.sample', { treated: r.sample.treatedUnits, control: r.sample.controlUnits })}{r.sampleFloor !== null ? ` · ${t('cpt.results.floor', { floor: r.sampleFloor })}` : ''}</span>,
                  },
                  {
                    key: 'stop', header: t('cpt.results.col.stop'),
                    render: (r: CockpitLeverResult) => {
                      const s = stateOf(r.lever, results.autoStops);
                      if (s === undefined) return <span>—</span>;
                      return (
                        <Stack gap={1}>
                          <Badge tone={autoStopTone(s.status)}>{v('measurement.auto_stop_status', s.status)}</Badge>
                          {s.status === 'STOPPED' && <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.results.stoppedAt', { when: when(s.stoppedAt), why: s.stopReasonCode === null ? '—' : reason(s.stopReasonCode) })}</span></Text>}
                          {s.status === 'WATCHING' && s.requiredWindows !== null && <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.results.watching', { n: s.consecutiveWindowsBelowFloor, of: s.requiredWindows })}</span></Text>}
                        </Stack>
                      );
                    },
                  },
                ]}
                rows={results.levers}
                rowKey={(r) => r.lever}
                empty={<EmptyState title={t('cpt.results.empty')} />}
              />
              <Text role="caption" tone="tertiary">{t('cpt.results.note')}</Text>
            </Stack>
          )}
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.sim.title')}</h3>
          <Text role="caption" tone="tertiary">{t('cpt.sim.hint')}</Text>
          {policy !== undefined && policy.value === null && <Notice tone="neutral">{t('cpt.sim.noPolicy')}</Notice>}
          {policy?.value != null && (
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void simulate()}>{t('cpt.sim.run')}</Button>
          )}
          {simError !== undefined && <Notice tone="danger">{simError}</Notice>}
          {sim !== undefined && (
            <Stack gap={3}>
              {sim.zonesRead === 0 && <Notice tone="warning">{t('cpt.rules.previewNoSignals')}</Notice>}
              <Table
                columns={[
                  { key: 'state', header: t('cpt.rules.col.state'), render: (r: ReturnType<typeof previewRows>[number]) => <span>{v('controller.zone_state', r.state)}</span> },
                  { key: 'draft', header: t('cpt.sim.col.time'), numeric: true, render: (r: ReturnType<typeof previewRows>[number]) => <span className="tnum">{t('cpt.rules.minutes', { minutes: windowsToMinutes(r.draft) })}</span> },
                ]}
                rows={previewRows(sim.draft, null)}
                rowKey={(r) => r.state}
                empty={<EmptyState title={t('cpt.rules.previewEmpty')} />}
              />
              <Table
                columns={[
                  { key: 'lever', header: t('cpt.rules.col.lever'), render: (r: ReturnType<typeof previewLeverRows>[number]) => <span>{v('active_incentive.lever', r.lever)}</span> },
                  { key: 'draft', header: t('cpt.sim.col.calls'), numeric: true, render: (r: ReturnType<typeof previewLeverRows>[number]) => <span className="tnum">{t('cpt.rules.minutes', { minutes: windowsToMinutes(r.draft) })}</span> },
                ]}
                rows={previewLeverRows(sim.draft, null)}
                rowKey={(r) => r.lever}
                empty={<EmptyState title={t('cpt.rules.previewEmpty')} />}
              />
              <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.sim.range', { from: formatMarketInstant(sim.from, tz, lang), to: formatMarketInstant(sim.to, tz, lang) })}</span></Text>
              <Text role="caption" tone="tertiary">{t('cpt.rules.previewScope')}</Text>
            </Stack>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
}
